import React, { useState } from 'react';
import './QuestionnaireDialog.css';
import { generateTitle } from '../../../services/title.service';

const QUESTIONS = [
  {
    id: 'q1',
    question: '1. What problem does your idea solve?',
    placeholder: 'Describe the core problem your solution addresses...',
    required: true,
  },
  {
    id: 'q2',
    question: '2. Who is your target audience/customer?',
    placeholder: 'Define your primary customer segments...',
    required: true,
  },
  {
    id: 'q3',
    question: '3. What makes your solution unique?',
    placeholder: 'Describe your competitive advantage and differentiation...',
    required: true,
  },
  {
    id: 'q4',
    question: '4. What is your revenue model?',
    placeholder: 'Explain how you plan to generate revenue...',
    required: false,
  },
  {
    id: 'q5',
    question: '5. What are the key technical requirements?',
    placeholder: 'List main technologies, infrastructure, and technical dependencies...',
    required: false,
  },
  {
    id: 'q6',
    question: '6. What is your go-to-market strategy?',
    placeholder: 'Describe how you plan to reach and acquire customers...',
    required: false,
  },
  {
    id: 'q7',
    question: '7. What are the major risks and challenges?',
    placeholder: 'Identify potential obstacles and how you plan to address them...',
    required: false,
  },
  {
    id: 'q8',
    question: '8. What is your expected timeline and budget?',
    placeholder: 'Provide estimates for development time and financial requirements...',
    required: false,
  },
];

const QuestionnaireDialog = ({ isOpen, onClose, onSubmit, formSettings }) => {
  const [answers, setAnswers] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [isGeneratingTitle, setIsGeneratingTitle] = useState(false);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value,
    }));
    
    // Clear error for this question
    if (errors[questionId]) {
      setErrors(prev => ({
        ...prev,
        [questionId]: null,
      }));
    }
  };

  const validateCurrentQuestion = () => {
    const currentQuestion = QUESTIONS[currentStep];
    if (currentQuestion.required && !answers[currentQuestion.id]?.trim()) {
      setErrors(prev => ({
        ...prev,
        [currentQuestion.id]: 'This question is required',
      }));
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validateCurrentQuestion()) {
      if (currentStep < QUESTIONS.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    // Validate all required questions
    const newErrors = {};
    QUESTIONS.forEach(q => {
      if (q.required && !answers[q.id]?.trim()) {
        newErrors[q.id] = 'This question is required';
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Find first error and go to that step
      const firstErrorIndex = QUESTIONS.findIndex(q => newErrors[q.id]);
      if (firstErrorIndex !== -1) {
        setCurrentStep(firstErrorIndex);
      }
      return;
    }

    // Create structured questionnaire data object
    const questionnaireData = {
      problem: answers.q1 || '',
      target_audience: answers.q2 || '',
      unique_value: answers.q3 || '',
      revenue_model: answers.q4 || '',
      technical_requirements: answers.q5 || '',
      go_to_market: answers.q6 || '',
      risks_challenges: answers.q7 || '',
      timeline_budget: answers.q8 || '',
    };

    // Generate clean title using LLM
    setIsGeneratingTitle(true);
    try {
      const cleanTitle = await generateTitle(
        questionnaireData, 
        null, 
        formSettings?.modelId
      );
      
      // Use clean title as the formatted idea
      onSubmit(cleanTitle, answers, questionnaireData);
    } catch (error) {
      console.error('Title generation failed, using fallback:', error);
      // Fallback to formatted answers if title generation fails
      const formattedIdea = formatAnswersToIdea(answers);
      onSubmit(formattedIdea, answers, questionnaireData);
    } finally {
      setIsGeneratingTitle(false);
      handleClose();
    }
  };

  const formatAnswersToIdea = (answers) => {
    let idea = '';
    
    QUESTIONS.forEach(q => {
      if (answers[q.id]?.trim()) {
        idea += `${q.question}\n${answers[q.id]}\n\n`;
      }
    });
    
    return idea.trim();
  };

  const handleClose = () => {
    setAnswers({});
    setCurrentStep(0);
    setErrors({});
    onClose();
  };

  const currentQuestion = QUESTIONS[currentStep];
  const progress = ((currentStep + 1) / QUESTIONS.length) * 100;
  const allRequiredAnswered = QUESTIONS
    .filter(q => q.required)
    .every(q => answers[q.id]?.trim());

  if (!isOpen) return null;

  return (
    <div className="questionnaire-overlay">
      <div className="questionnaire-container">
        {/* Main Dialog */}
        <div className="questionnaire-dialog">
          <div className="questionnaire-header">
            <h2>📋 8-Question Idea Analysis</h2>
            <button className="close-btn" onClick={handleClose}>×</button>
          </div>

          {/* Settings Info Banner */}
          {formSettings && (
            <div className="settings-info-banner">
              <div className="settings-item">
                <span className="settings-label">Backend:</span>
                <span className="settings-value">{formSettings.backend === 'strands' ? '🔗 Strands (Multi-Agent)' : '⚡ Traditional'}</span>
              </div>
              <div className="settings-item">
                <span className="settings-label">Mode:</span>
                <span className="settings-value">{formSettings.advancedMode ? '📊 Advanced (8 sections)' : '📄 Standard (5 sections)'}</span>
              </div>
              {formSettings.backend === 'strands' && (
                <div className="settings-item">
                  <span className="settings-label">Workflow:</span>
                  <span className="settings-value">{formSettings.workflowType}</span>
                </div>
              )}
            </div>
          )}

          {/* Progress Bar */}
          <div className="progress-container">
            <div className="progress-bar" style={{ width: `${progress}%` }}></div>
            <span className="progress-text">
              Question {currentStep + 1} of {QUESTIONS.length}
            </span>
          </div>

          {/* Current Question */}
          <div className="question-container">
            <div className="question-header">
              <h3>{currentQuestion.question}</h3>
              {currentQuestion.required && <span className="required-badge">Required</span>}
            </div>

            <textarea
              className={`question-textarea ${errors[currentQuestion.id] ? 'error' : ''}`}
              value={answers[currentQuestion.id] || ''}
              onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
              placeholder={currentQuestion.placeholder}
              rows={6}
              autoFocus
            />

            {errors[currentQuestion.id] && (
              <div className="error-message">
                {errors[currentQuestion.id]}
              </div>
            )}

            {/* Answer Counter */}
            <div className="answer-stats">
              <span>
                {answers[currentQuestion.id]?.length || 0} characters
              </span>
              <span>
                {Object.keys(answers).filter(k => answers[k]?.trim()).length} / {QUESTIONS.length} answered
              </span>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="questionnaire-actions">
            <div className="left-actions">
              <button
                type="button"
                className="nav-button secondary"
                onClick={handlePrevious}
                disabled={currentStep === 0}
              >
                ← Previous
              </button>
            </div>

            <div className="right-actions">
              {currentStep < QUESTIONS.length - 1 ? (
                <button
                  type="button"
                  className="nav-button primary"
                  onClick={handleNext}
                >
                  Next →
                </button>
              ) : (
                <button
                  type="button"
                  className="nav-button primary generate"
                  onClick={handleSubmit}
                  disabled={!allRequiredAnswered || isGeneratingTitle}
                >
                  {isGeneratingTitle ? '⏳ Preparing...' : '🚀 Generate Report'}
                </button>
              )}
            </div>
          </div>

          {/* Question Navigator */}
          <div className="question-navigator">
            {QUESTIONS.map((q, index) => (
              <button
                key={q.id}
                className={`question-dot ${index === currentStep ? 'active' : ''} ${
                  answers[q.id]?.trim() ? 'answered' : ''
                } ${errors[q.id] ? 'error' : ''}`}
                onClick={() => setCurrentStep(index)}
                title={q.question}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Side Preview Panel */}
        {Object.keys(answers).length > 0 && (
          <div className="preview-panel">
            <div className="preview-header">
              <h3>📝 Your Answers</h3>
              <span className="preview-count">
                {Object.keys(answers).filter(k => answers[k]?.trim()).length} / {QUESTIONS.length}
              </span>
            </div>
            <div className="preview-content">
              {QUESTIONS.map(q => answers[q.id]?.trim() && (
                <div key={q.id} className="preview-item">
                  <div className="preview-question">{q.question}</div>
                  <div className="preview-answer">{answers[q.id]}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionnaireDialog;
